import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  index?: boolean;
  lang?: 'pt-BR' | 'es' | 'en';
}

export default function SEO({ 
  title, 
  description, 
  canonical, 
  index = true,
  lang
}: SEOProps) {
  const { language } = useLanguage();
  const siteName = 'TechEnabler';
  const motto = 'We sell, we connect, we integrate';
  const fullTitle = title.toLowerCase() === 'home' || title === siteName 
    ? `${siteName} - ${motto}` 
    : `${title} - ${siteName}`;
  const baseUrl = 'https://techenabler.com.br'; // URL base institucional
  const currentCanonical = canonical ? `${baseUrl}${canonical}` : undefined;
  
  // Use a linguagem do contexto se lang não for explicitamente definido
  const activeLang = lang || (language === 'pt' ? 'pt-BR' : language === 'en' ? 'en' : 'es');

  return (
    <Helmet htmlAttributes={{ lang: activeLang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      
      {/* Indexação */}
      {!index && <meta name="robots" content="noindex, nofollow" />}
      {index && <meta name="robots" content="index, follow" />}

      {/* Canonical */}
      {currentCanonical && <link rel="canonical" href={currentCanonical} />}

      {/* Hreflang: o site tem uma única URL por página (idioma trocado no client, sem
          rotas /es ou /en), então o hreflang correto é autorreferenciado + x-default */}
      {currentCanonical && <link rel="alternate" hrefLang="pt-br" href={currentCanonical} />}
      {currentCanonical && <link rel="alternate" hrefLang="x-default" href={currentCanonical} />}

      {/* Open Graph / Social */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      {currentCanonical && <meta property="og:url" content={currentCanonical} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}
